import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout-admin";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Shield, Building2, FileText, Users, AlertTriangle, CheckCircle, Clock, DollarSign } from "lucide-react";
import { useState } from "react";
import type { PayerTaxProfile, Form1099Summary } from "@shared/schema";

function formatMoney(val: string | number | null | undefined) {
  const n = Number(val || 0);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function PayerProfileTab() {
  const { toast } = useToast();
  const { data: profile, isLoading } = useQuery<PayerTaxProfile>({
    queryKey: ["/api/admin/payer-tax-profile"],
  });

  const [form, setForm] = useState<Partial<PayerTaxProfile>>({});
  const [initialized, setInitialized] = useState(false);

  if (profile && !initialized) {
    setForm({
      legalBusinessName: profile.legalBusinessName || "",
      ein: "",
      address: profile.address || "",
      city: profile.city || "",
      state: profile.state || "",
      zipCode: profile.zipCode || "",
      contactName: profile.contactName || "",
      contactPhone: profile.contactPhone || "",
      contactEmail: profile.contactEmail || "",
      transmitterControlCode: profile.transmitterControlCode || "",
    });
    setInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<PayerTaxProfile>) => {
      const res = await apiRequest("PUT", "/api/admin/payer-tax-profile", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/payer-tax-profile"] });
      toast({ title: "Tax profile saved" });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" data-testid="text-payer-profile-title">
            <Building2 className="h-5 w-5" /> Payer Tax Profile
          </CardTitle>
          <CardDescription>Your organization's business information for 1099 filing. This appears on all 1099-NEC forms issued to contractors.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Legal Business Name</Label>
              <Input
                data-testid="input-legal-business-name"
                value={form.legalBusinessName || ""}
                onChange={(e) => setForm({ ...form, legalBusinessName: e.target.value })}
                placeholder="Your Business LLC"
              />
            </div>
            <div className="space-y-2">
              <Label>EIN (Employer Identification Number)</Label>
              <Input
                data-testid="input-ein"
                value={form.ein || ""}
                onChange={(e) => setForm({ ...form, ein: e.target.value })}
                placeholder={profile?.einLast4 ? `••••${profile.einLast4}` : "XX-XXXXXXX"}
                type="password"
              />
              {profile?.einLast4 && (
                <p className="text-xs text-muted-foreground">Last 4 on file: •••{profile.einLast4}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Street Address</Label>
              <Input data-testid="input-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>City</Label>
                <Input data-testid="input-city" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input data-testid="input-state" value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="FL" />
              </div>
              <div className="space-y-2">
                <Label>ZIP</Label>
                <Input data-testid="input-zip" value={form.zipCode || ""} onChange={(e) => setForm({ ...form, zipCode: e.target.value })} placeholder="33101" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Contact Name</Label>
              <Input data-testid="input-contact-name" value={form.contactName || ""} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Contact Phone</Label>
              <Input data-testid="input-contact-phone" value={form.contactPhone || ""} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input data-testid="input-contact-email" value={form.contactEmail || ""} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Transmitter Control Code (TCC)</Label>
            <Input data-testid="input-tcc" value={form.transmitterControlCode || ""} onChange={(e) => setForm({ ...form, transmitterControlCode: e.target.value })} placeholder="Optional - for e-filing" />
            <p className="text-xs text-muted-foreground">Required only if you plan to e-file 1099s with the IRS.</p>
          </div>
          <Button
            data-testid="button-save-profile"
            onClick={() => saveMutation.mutate(form)}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Tax Profile
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Form1099Tab() {
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear - 1);
  const [showGenerate, setShowGenerate] = useState(false);

  const { data: summaries, isLoading } = useQuery<Form1099Summary[]>({
    queryKey: ["/api/admin/1099-summaries", taxYear],
    queryFn: async () => {
      const res = await fetch(`/api/admin/1099-summaries?taxYear=${taxYear}`);
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const { data: reconciliation, isLoading: reconLoading } = useQuery<any>({
    queryKey: ["/api/admin/contractor-reconciliation", taxYear],
    queryFn: async () => {
      const res = await fetch(`/api/admin/contractor-reconciliation?taxYear=${taxYear}`);
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (data: { contractorUserId: number; taxYear: number; totalNonemployeeComp: string }) => {
      const res = await apiRequest("POST", "/api/admin/1099-summaries", {
        ...data,
        organizationId: 0,
        filingStatus: "draft",
        totalReimbursements: "0",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/1099-summaries"] });
      setShowGenerate(false);
      toast({ title: "1099 summary created with auto-calculated compensation" });
    },
    onError: () => toast({ title: "Failed to create", variant: "destructive" }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, filingStatus }: { id: number; filingStatus: string }) => {
      const res = await apiRequest("PUT", `/api/admin/1099-summaries/${id}`, { filingStatus });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/1099-summaries"] });
      toast({ title: "Status updated" });
    },
  });

  const statusColor = (status: string) => {
    switch (status) {
      case "draft": return "secondary" as const;
      case "ready": return "default" as const;
      case "filed": return "default" as const;
      case "accepted": return "default" as const;
      case "delivered": return "default" as const;
      default: return "secondary" as const;
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case "draft": return <Clock className="h-3 w-3" />;
      case "ready": return <AlertTriangle className="h-3 w-3" />;
      case "filed": return <FileText className="h-3 w-3" />;
      case "accepted": return <CheckCircle className="h-3 w-3" />;
      case "delivered": return <CheckCircle className="h-3 w-3" />;
      default: return null;
    }
  };

  const reconContractors = reconciliation?.contractors || [];
  const existingSummaryUserIds = new Set((summaries || []).map((s: any) => s.contractorUserId));
  const eligibleForGeneration = reconContractors.filter((c: any) => !existingSummaryUserIds.has(c.userId));

  const getContractorName = (userId: number) => {
    const c = reconContractors.find((c: any) => c.userId === userId);
    return c ? c.fullName : `Contractor #${userId}`;
  };

  return (
    <div className="space-y-6">
      {reconLoading && (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      )}
      {!reconLoading && reconciliation && reconContractors.length > 0 && (
        <Card className="border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base" data-testid="text-recon-title">
              <Users className="h-5 w-5" /> Auto-Reconciliation — {taxYear}
            </CardTitle>
            <CardDescription>
              Compensation calculated from cleaning jobs and direct payments in the system.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-background rounded-lg border">
                <p className="text-2xl font-bold" data-testid="text-total-contractors">{reconciliation.totals.totalContractors}</p>
                <p className="text-xs text-muted-foreground">Total Contractors</p>
              </div>
              <div className="text-center p-3 bg-background rounded-lg border">
                <p className="text-2xl font-bold text-orange-600" data-testid="text-requiring-1099">{reconciliation.totals.contractorsRequiring1099}</p>
                <p className="text-xs text-muted-foreground">Require 1099</p>
              </div>
              <div className="text-center p-3 bg-background rounded-lg border">
                <p className="text-2xl font-bold" data-testid="text-total-compensation">{formatMoney(reconciliation.totals.totalCompensation)}</p>
                <p className="text-xs text-muted-foreground">Total Compensation</p>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contractor</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Cleaning Earnings</TableHead>
                  <TableHead>Direct Payments</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>1099?</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reconContractors.map((c: any) => (
                  <TableRow key={c.userId} data-testid={`row-recon-${c.userId}`}>
                    <TableCell className="font-medium">{c.fullName}</TableCell>
                    <TableCell className="capitalize">{c.role}</TableCell>
                    <TableCell className="font-mono">{formatMoney(c.cleaningEarnings)}</TableCell>
                    <TableCell className="font-mono">{formatMoney(c.directPayments)}</TableCell>
                    <TableCell className="font-mono font-bold">{formatMoney(c.totalCompensation)}</TableCell>
                    <TableCell>
                      {c.requires1099 ? (
                        <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />Required</Badge>
                      ) : (
                        <Badge variant="secondary">Under $600</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Label>Tax Year</Label>
          <Select value={String(taxYear)} onValueChange={(v) => setTaxYear(Number(v))}>
            <SelectTrigger className="w-32" data-testid="select-tax-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[currentYear - 1, currentYear - 2, currentYear - 3, currentYear].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
          <DialogTrigger asChild>
            <Button data-testid="button-generate-1099">
              <FileText className="h-4 w-4 mr-2" />
              Generate 1099
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate 1099-NEC Summary</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <p className="text-sm text-muted-foreground">
                Select a contractor to auto-generate a 1099-NEC summary for tax year {taxYear}. 
                Amounts are calculated from cleaning earnings and direct payments in the system.
              </p>
              {eligibleForGeneration.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {reconContractors.length === 0
                    ? "No contractors found. Add cleaner or maintenance users first."
                    : "All contractors already have 1099 summaries for this year."}
                </p>
              ) : (
                <div className="space-y-2">
                  {eligibleForGeneration.map((c: any) => (
                    <Button
                      key={c.userId}
                      variant="outline"
                      className="w-full justify-between"
                      data-testid={`button-generate-for-${c.userId}`}
                      disabled={generateMutation.isPending}
                      onClick={() => generateMutation.mutate({
                        contractorUserId: c.userId,
                        taxYear,
                        totalNonemployeeComp: c.totalCompensation,
                      })}
                    >
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {c.fullName} ({c.role})
                      </span>
                      <span className="font-mono text-sm">
                        {formatMoney(c.totalCompensation)}
                        {c.requires1099 && <AlertTriangle className="h-3 w-3 inline ml-1 text-orange-500" />}
                      </span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" data-testid="text-1099-title">
            <FileText className="h-5 w-5" /> 1099-NEC Summaries — {taxYear}
          </CardTitle>
          <CardDescription>
            Contractors who received $600+ in non-employee compensation require a 1099-NEC filing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : !summaries || summaries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="text-no-1099s">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No 1099 summaries for {taxYear}.</p>
              <p className="text-sm mt-1">Click "Generate 1099" to auto-create from reconciled payment data.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contractor</TableHead>
                  <TableHead>Non-Employee Comp</TableHead>
                  <TableHead>Reimbursements</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaries.map((s) => (
                  <TableRow key={s.id} data-testid={`row-1099-${s.id}`}>
                    <TableCell>
                      <div className="font-medium">{getContractorName(s.contractorUserId)}</div>
                      {!!s.w9SnapshotData && <div className="text-xs text-muted-foreground">W-9 on file</div>}
                    </TableCell>
                    <TableCell className="font-mono">{formatMoney(s.totalNonemployeeComp)}</TableCell>
                    <TableCell className="font-mono">{formatMoney(s.totalReimbursements)}</TableCell>
                    <TableCell>
                      <Badge variant={statusColor(s.filingStatus || "draft")} className="gap-1">
                        {statusIcon(s.filingStatus || "draft")}
                        {s.filingStatus || "draft"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={s.filingStatus || "draft"}
                        onValueChange={(v) => updateStatusMutation.mutate({ id: s.id, filingStatus: v })}
                      >
                        <SelectTrigger className="w-28 h-8" data-testid={`select-status-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="ready">Ready</SelectItem>
                          <SelectItem value="filed">Filed</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ContractorPaymentsTab() {
  const { toast } = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    contractorUserId: "",
    amount: "",
    method: "check",
    description: "",
    datePaid: new Date().toISOString().split("T")[0],
  });

  const { data: payments, isLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/contractor-payments"],
  });

  const { data: users } = useQuery<any[]>({
    queryKey: ["/api/admin/users"],
  });

  const contractorUsers = (users || []).filter((u: any) => u.role === "cleaner" || u.role === "maintenance" || ((u.additionalRoles as string[] | undefined) ?? []).some(r => r === "cleaner" || r === "maintenance"));

  const addMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/contractor-payments", {
        ...data,
        contractorUserId: Number(data.contractorUserId),
        amount: data.amount,
        datePaid: new Date(data.datePaid).toISOString(),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contractor-payments"] });
      setShowAdd(false);
      setPaymentForm({ contractorUserId: "", amount: "", method: "check", description: "", datePaid: new Date().toISOString().split("T")[0] });
      toast({ title: "Payment recorded" });
    },
    onError: () => toast({ title: "Failed to record payment", variant: "destructive" }),
  });

  const voidMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/admin/contractor-payments/${id}/void`, { reason: "Voided by admin" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/contractor-payments"] });
      toast({ title: "Payment voided" });
    },
  });

  const contractorName = (id: number) => {
    const u = (users || []).find((u: any) => u.id === id);
    return u ? (u.fullName || u.username) : `#${id}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-payment">
              <DollarSign className="h-4 w-4 mr-2" /> Record Payment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Contractor Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Contractor</Label>
                <Select value={paymentForm.contractorUserId} onValueChange={(v) => setPaymentForm({ ...paymentForm, contractorUserId: v })}>
                  <SelectTrigger data-testid="select-contractor">
                    <SelectValue placeholder="Select contractor" />
                  </SelectTrigger>
                  <SelectContent>
                    {contractorUsers.map((u: any) => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.fullName || u.username} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount ($)</Label>
                  <Input data-testid="input-payment-amount" type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date Paid</Label>
                  <Input data-testid="input-date-paid" type="date" value={paymentForm.datePaid} onChange={(e) => setPaymentForm({ ...paymentForm, datePaid: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                  <SelectTrigger data-testid="select-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="ach">ACH</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="venmo">Venmo</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea data-testid="input-payment-description" value={paymentForm.description} onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })} placeholder="Optional notes" />
              </div>
              <Button
                data-testid="button-submit-payment"
                className="w-full"
                disabled={!paymentForm.contractorUserId || !paymentForm.amount || addMutation.isPending}
                onClick={() => addMutation.mutate(paymentForm)}
              >
                {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Record Payment
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" data-testid="text-payments-title">
            <DollarSign className="h-5 w-5" /> Contractor Payment Ledger
          </CardTitle>
          <CardDescription>All payments to contractors are tracked here for 1099 reporting.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : !payments || payments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="text-no-payments">
              <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No contractor payments recorded yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Contractor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p: any) => (
                  <TableRow key={p.id} data-testid={`row-payment-${p.id}`}>
                    <TableCell className="text-sm">{new Date(p.datePaid).toLocaleDateString("en-US", { timeZone: "UTC" })}</TableCell>
                    <TableCell className="font-medium">{contractorName(p.contractorUserId)}</TableCell>
                    <TableCell className="font-mono">{formatMoney(p.amount)}</TableCell>
                    <TableCell className="capitalize">{p.method}</TableCell>
                    <TableCell>
                      <Badge variant={p.status === "paid" ? "default" : "destructive"}>
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {p.status === "paid" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive h-7"
                          data-testid={`button-void-${p.id}`}
                          onClick={() => voidMutation.mutate(p.id)}
                        >
                          Void
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminTaxCompliance() {
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2" data-testid="text-page-title">
            <Shield className="h-6 w-6" /> Tax Compliance & 1099
          </h1>
          <p className="text-muted-foreground mt-1">Manage payer tax profiles, contractor payments, and 1099-NEC filing.</p>
        </div>

        <Tabs defaultValue="profile">
          <TabsList data-testid="tax-tabs">
            <TabsTrigger value="profile" data-testid="tab-profile">Payer Profile</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Contractor Payments</TabsTrigger>
            <TabsTrigger value="1099" data-testid="tab-1099">1099 Forms</TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="mt-4">
            <PayerProfileTab />
          </TabsContent>
          <TabsContent value="payments" className="mt-4">
            <ContractorPaymentsTab />
          </TabsContent>
          <TabsContent value="1099" className="mt-4">
            <Form1099Tab />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
